import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import ContentDetailPage from '@/views/page/ContentDetailPage.vue'
import Collection from '@/views/page/Collection.vue'
import RekionTopPage from '../views/rekion/RekionTopPage.vue'
import RekionSearchResult from '../views/rekion/RekionSearchResult.vue'

// tracking
import tracker from '@/helpers/tracking'
import { store } from '@/store'
import { createFromParameter, createToParameter, getCardId } from '@/store/modules/StatisticsLog'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'RekionTopPage',
    component: RekionTopPage,
    meta: {
      isRekion: true,
      pageTitle: { en: 'Historical Recordings Collection', ja: '歴史的音源' },
      description: { en: 'description - Historical Recordings Collection', ja: 'description - 歴史的音源' },
      ogType: 'website',
    }
  },
  {
    path: '/search/result',
    name: 'RekionSearchResult',
    component: RekionSearchResult,
    meta: {
      isRekion: true,
      pageTitle: { en: 'Search result - Historical Recordings Collection', ja: '検索結果 - 歴史的音源' },
      description: { en: '(description)Search result - Historical Recordings Collection', ja: '(description)検索結果 - 歴史的音源' },
      ogType: 'article',
    },
    props: route => ({
      pageNo: route.query.pageNo,
      component: 'TheSearchResultMain'
    })
  },
  {
    path: '/pid/:pid(\\d+)/:bundleIndex(\\d+)?/:contentIndex(\\d+)?',
    name: 'ContentDetail',
    component: ContentDetailPage,
    meta: {
      isRekion: true,
      pageTitle: { en: 'Item detail - Historical Recordings Collection', ja: 'アイテム詳細画面 - 歴史的音源' },
      description: { en: '(description)Item detail - Historical Recordings Collection', ja: '(description)アイテム詳細画面 - 歴史的音源' },
      ogType: 'article',
    }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('../views/page/Static.vue' /* webpackChunkName: "static" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Help - Historical Recordings Collection', ja: 'ヘルプ - 歴史的音源' },
      description: { en: '(description)Help - Historical Recordings Collection', ja: '(description)ヘルプ - 歴史的音源' },
      ogType: 'article',
    },
    props: {
      htmlName: 'help.html'
    }
  },
  {
    path: '/sitepolicy',
    name: 'Sitepolicy',
    component: () => import('../views/page/Static.vue' /* webpackChunkName: "static" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Site Policy - Historical Recordings Collection', ja: 'サイトポリシー - 歴史的音源' },
      description: { en: '(description)Site Policy - Historical Recordings Collection', ja: '(description)サイトポリシー - 歴史的音源' },
      ogType: 'article',
    },
    props: {
      htmlName: 'sitepolicy.html'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/page/Static.vue' /* webpackChunkName: "static" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Contact - Historical Recordings Collection', ja: 'コンタクト - 歴史的音源' },
      description: { en: '(description)Contact - Historical Recordings Collection', ja: '(description)コンタクト - 歴史的音源' },
      ogType: 'article',
    },
    props: {
      htmlName: 'contact.html'
    }
  },
  {
    path: '/information',
    name: 'Information',
    component: () => import('../views/page/Static.vue' /* webpackChunkName: "static" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Information - Historical Recordings Collection', ja: 'インフォメーション - 歴史的音源' },
      description: { en: '(description)Information - Historical Recordings Collection', ja: '(description)インフォメーション - 歴史的音源' },
      ogType: 'article',
    },
    props: {
      htmlName: 'information.html'
    }
  },
  {
    path: '/collection',
    name: 'Collection',
    component: Collection,
    meta: {
      isRekion: true,
      pageTitle: { en: 'Collection - Historical Recordings Collection', ja: 'コレクション - 歴史的音源' },
      description: { en: '(description)Collection - Historical Recordings Collection', ja: '(description)コレクション - 歴史的音源' },
      ogType: 'article',
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/page/BuiltinLogin.vue' /* webpackChunkName: "builtin-login" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Login', ja: 'ログイン' },
      description: { en: '', ja: '' },
      ogType: 'article',
    }
  },
  {
    // path,chunknameに"admin"が含まれるとS3で403 Forbiddenとなるため、manageとする。
    path: '/manage',
    name: 'AdminMenu',
    component: () => import('../views/rekion/RekionAdminMenuPage.vue' /* webpackChunkName: "rekion-manage-menu" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Rekion administrator menu', ja: 'れきおん管理者メニュー' },
      description: { en: '', ja: '' },
      ogType: 'article',
    }
  },
  {
    path: '/tool/login',
    name: 'ToolLogin',
    component: () => import('../views/page/ToolLoginPage.vue' /* webpackChunkName: "builtin-login-tool" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Auto Login With Login Tool', ja: 'ログインツールでの自動ログイン' },
      description: { en: '', ja: '' },
      ogType: 'article',
    }
  },
  {
    path: '/statistics',
    name: 'Statistics',
    component: () => import('@/views/rekion/StatisticsPage.vue'),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Rekion Get Statistics', ja: 'れきおん統計情報取得' },
      description: { en: '', ja: '' },
      ogType: 'article',
    }
  },
  {
    path: '/info\\:ndljp/pid/:pid(\\d+)/:contentIndex(\\d+)?',
    redirect: (to) => {
      const contentIndex = to.params.contentIndex ? to.params.contentIndex : '1'
      return `/pid/${to.params.pid}/1/${contentIndex}`
    }
  },
  // routeの追加はこれより上に行う
  {
    path: '/:static(.*)',
    name: 'StaticPage',
    component: () => import('../views/page/Static.vue' /* webpackChunkName: "static" */),
    meta: {
      isRekion: true,
      pageTitle: { en: 'Historical Recordings Collection', ja: '歴史的音源' },
      description: { en: '(description)', ja: '(description)' },
      ogType: 'article',
    }
  }
]

const setI18nAlias = (routes: Array<RouteRecordRaw>): Array<RouteRecordRaw> => {
  return routes.map(route => {
    let children
    if (route.children && route.children.length > 0) {
      children = setI18nAlias(route.children)
    }
    return {
      ...route,
      children,
      alias: ['/ja' + route.path, '/en' + route.path]
    }
  })
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: setI18nAlias(routes),
  scrollBehavior: async (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition
    }

    const findElement = async (hash: string, times = 0): Promise<Element | undefined> => {
      if (!document) return // サーバーサイド実行時
      if (!hash || hash === '#') return // アンカーリンクがない時
      if (times > 20) return // 20回のリトライで対象要素が見つからない時
      if (times === 0) { // 初回画面遷移時
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(findElement(hash, ++times))
          }, 1500) // 画面が描画され終わるまで待機する
        })
      }

      return document.querySelector(hash) ||
          new Promise((resolve) => {
            setTimeout(() => {
              resolve(findElement(hash, ++times))
            }, 100)
          })
    }

    // findElementは初回1500ms待機するが、ページ内遷移では初回待機させないため
    const isFirstPageVisit = Boolean(to.path !== from.path || from.name)
    const targetElement = (await findElement(to.hash, Number(isFirstPageVisit)) as HTMLElement|undefined)
    if (!targetElement) return { left: 0, top: 0 }
    if ('scrollBehavior' in document.documentElement.style) {
      return window.scrollTo({ top: targetElement.offsetTop, behavior: 'smooth' })
    } else {
      return window.scrollTo(0, targetElement.offsetTop)
    }
  },
})

router.beforeEach(async (to, from, next) => {
  // セッションチェックをする
  if (!await store.dispatch('sessionCheck')) next('/')

  const isJapanesePath = (path: string) => path.startsWith('/ja/') || path === '/ja'
  const isEnglishPath = (path: string) => path.startsWith('/en/') || path === '/en'

  // ページごとにメタを設定する
  if (isJapanesePath(to.fullPath)) to.meta.lang = 'ja'
  if (isEnglishPath(to.fullPath)) to.meta.lang = 'en'
  // 必要に応じてURLを書き換えて遷移する
  if (isJapanesePath(from.fullPath) && !isJapanesePath(to.fullPath)) {
    next(isEnglishPath(to.fullPath) ? to.fullPath.replace(/^\/en/, '/ja') : '/ja' + to.fullPath)
    return
  }
  if (isEnglishPath(from.fullPath) && !isEnglishPath(to.fullPath)) {
    next(isJapanesePath(to.fullPath) ? to.fullPath.replace(/^\/ja/, '/en') : '/en' + to.fullPath)
    return
  }
  next()
})

router.afterEach((to, from, failure) => {
  const cardId = getCardId(store)
  const toMeta = createToParameter(to, cardId)
  const fromMeta = createFromParameter(from, cardId)
  tracker.push({ key: 'router', to: toMeta, from: fromMeta })
  if (cardId != null) {
    tracker.push({ key: 'router', to: toMeta, from: fromMeta, cardId: cardId })
  }
})

export default router
